import { bytesToBase64, bytesToBase64Url, base64ToBytes, base64UrlToBytes } from './encoding';

/**
 * ECIES-like hybrid encryption for the recovery-contact feature.
 *
 * Protocol shape: same as familyHandoffCrypto.ts (ephemeral X25519 +
 * HKDF-SHA-256 + AES-256-GCM) but with parameterised info labels so wraps
 * can't be replayed across purposes (vault-key vs account-key, designation
 * vs reset step).
 *
 * The recipient's public key is embedded in the bundle so decryption only
 * requires the private key — Web Crypto X25519 privkeys are imported
 * non-extractable and the pubkey cannot be recovered from the privkey alone.
 *
 * Domain separation labels (all distinct from existing ones):
 *   "recovery-vault-key-v1"        — vault key wrapped to contact at designation
 *   "recovery-account-key-v1"      — account privkey wrapped to contact at designation
 *   "recovery-reset-vault-key-v1"  — vault key wrapped to ephemeral at reset
 *   "recovery-reset-account-key-v1"— account privkey wrapped to ephemeral at reset
 */

export type RecoveryWrapLabel =
  | 'recovery-vault-key-v1'
  | 'recovery-account-key-v1';

export type RecoveryResetWrapLabel =
  | 'recovery-reset-vault-key-v1'
  | 'recovery-reset-account-key-v1';

export interface RecoveryBundle {
  ciphertext: string;         // base64
  iv: string;                 // base64 (12 bytes)
  ephemeralPublicKey: string; // base64url (32 bytes)
  recipientPublicKey: string; // base64url (32 bytes) — stored so decrypt only needs the privkey
}

/**
 * Wrap a 32-byte key (vault key or account private key) to the recovery
 * contact's account public key. The info label binds the wrap to its purpose.
 */
export async function wrapForRecoveryContact(
  payload: Uint8Array,
  contactPublicKey: Uint8Array,
  label: RecoveryWrapLabel,
): Promise<RecoveryBundle> {
  return eciesEncrypt(payload, contactPublicKey, label);
}

export async function unwrapForRecoveryContact(
  bundle: RecoveryBundle,
  contactPrivateKey: Uint8Array,
  label: RecoveryWrapLabel,
): Promise<Uint8Array> {
  return eciesDecrypt(bundle, contactPrivateKey, label);
}

/**
 * Wrap a 32-byte key to the per-reset ephemeral public key generated on the
 * requester's device. Used by the contact during the forgot-password approval step.
 */
export async function wrapForResetEphemeral(
  payload: Uint8Array,
  ephemeralPublicKey: Uint8Array,
  label: RecoveryResetWrapLabel,
): Promise<RecoveryBundle> {
  return eciesEncrypt(payload, ephemeralPublicKey, label);
}

export async function unwrapForResetEphemeral(
  bundle: RecoveryBundle,
  ephemeralPrivateKey: Uint8Array,
  label: RecoveryResetWrapLabel,
): Promise<Uint8Array> {
  return eciesDecrypt(bundle, ephemeralPrivateKey, label);
}

// ---------------------------------------------------------------------------
// Internal ECIES primitive
// ---------------------------------------------------------------------------

async function eciesEncrypt(
  payload: Uint8Array,
  recipientPublicKey: Uint8Array,
  label: string,
): Promise<RecoveryBundle> {
  // 1. Ephemeral X25519 keypair
  const ephKp = (await crypto.subtle.generateKey(
    { name: 'X25519' },
    true,
    ['deriveBits'],
  )) as CryptoKeyPair;

  const ephPubJwk = await crypto.subtle.exportKey('jwk', ephKp.publicKey);
  const ephPubBytes = base64UrlToBytes(ephPubJwk.x!);

  // 2. Import recipient pubkey for ECDH
  const recipientPubKey = await crypto.subtle.importKey(
    'jwk',
    { kty: 'OKP', crv: 'X25519', x: bytesToBase64Url(recipientPublicKey) },
    { name: 'X25519' },
    true,
    [],
  );

  // 3. ECDH → shared secret
  const sharedSecret = await crypto.subtle.deriveBits(
    { name: 'X25519', public: recipientPubKey },
    ephKp.privateKey,
    256,
  );

  // 4. HKDF: salt = eph_pub || recipient_pub, info = label
  const aesKey = await deriveAesKey(sharedSecret, ephPubBytes, recipientPublicKey, label, 'encrypt');

  // 5. AES-256-GCM encrypt
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, aesKey, payload as BufferSource),
  );

  return {
    ciphertext: bytesToBase64(ciphertext),
    iv: bytesToBase64(iv),
    ephemeralPublicKey: bytesToBase64Url(ephPubBytes),
    recipientPublicKey: bytesToBase64Url(recipientPublicKey),
  };
}

async function eciesDecrypt(
  bundle: RecoveryBundle,
  recipientPrivateKey: Uint8Array,
  label: string,
): Promise<Uint8Array> {
  const recipientPubBytes = base64UrlToBytes(bundle.recipientPublicKey);
  const ephPubBytes = base64UrlToBytes(bundle.ephemeralPublicKey);

  // Import recipient keypair for ECDH
  const recipientPrivKey = await crypto.subtle.importKey(
    'jwk',
    {
      kty: 'OKP',
      crv: 'X25519',
      x: bundle.recipientPublicKey,
      d: bytesToBase64Url(recipientPrivateKey),
    } as JsonWebKey,
    { name: 'X25519' },
    false,
    ['deriveBits'],
  );

  // Import ephemeral pubkey
  const ephPubKey = await crypto.subtle.importKey(
    'jwk',
    { kty: 'OKP', crv: 'X25519', x: bundle.ephemeralPublicKey },
    { name: 'X25519' },
    true,
    [],
  );

  // ECDH → shared secret
  const sharedSecret = await crypto.subtle.deriveBits(
    { name: 'X25519', public: ephPubKey },
    recipientPrivKey,
    256,
  );

  // Re-derive AES key with same salt + label
  const aesKey = await deriveAesKey(sharedSecret, ephPubBytes, recipientPubBytes, label, 'decrypt');

  // AES-256-GCM decrypt
  const ivBytes = base64ToBytes(bundle.iv);
  const ctBytes = base64ToBytes(bundle.ciphertext);
  return new Uint8Array(
    await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: ivBytes.buffer as ArrayBuffer },
      aesKey,
      ctBytes.buffer as ArrayBuffer,
    ),
  );
}

async function deriveAesKey(
  sharedSecret: ArrayBuffer,
  ephPubBytes: Uint8Array,
  recipientPubBytes: Uint8Array,
  label: string,
  usage: 'encrypt' | 'decrypt',
): Promise<CryptoKey> {
  const salt = new Uint8Array(64);
  salt.set(ephPubBytes, 0);
  salt.set(recipientPubBytes, 32);

  const hkdfKeyMaterial = await crypto.subtle.importKey(
    'raw', sharedSecret, 'HKDF', false, ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt, info: new TextEncoder().encode(label) },
    hkdfKeyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    [usage],
  );
}

